import React from 'react';
import { useTranslation } from 'localization';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InputAtom from '../../atoms/Input/InputAtom';

export interface DataListHeaderProps {
  /** Title text displayed in the header */
  title: string;
  /** Icon class (e.g., "pi pi-list") */
  icon?: string;
  /** Whether the header can be collapsed */
  collapsible?: boolean;
  /** Current expanded state */
  isExpanded?: boolean;
  /** Callback when expand/collapse is toggled */
  onToggle?: () => void;
  /** Item count to display next to title */
  itemCount?: number;
  /** Show search input */
  showSearch?: boolean;
  /** Current search value */
  searchValue?: string;
  /** Callback when search value changes */
  onSearchChange?: (value: string) => void;
  /** Search input placeholder */
  searchPlaceholder?: string;
  /** Show refresh button */
  showRefresh?: boolean;
  /** Callback when refresh is clicked */
  onRefresh?: () => void;
  /** Whether refresh is in progress */
  refreshing?: boolean;
  /** Show settings button */
  showSettings?: boolean;
  /** Callback when settings button is clicked */
  onSettingsClick?: () => void;
  /** Additional header actions (rendered after built-in actions) */
  headerActions?: React.ReactNode;
  /** Custom filter controls rendered after the search input */
  customFilters?: React.ReactNode;
}

const DataListHeader: React.FC<DataListHeaderProps> = ({
  title,
  icon,
  collapsible = false,
  isExpanded = true,
  onToggle,
  itemCount,
  showSearch = false,
  searchValue = '',
  onSearchChange,
  searchPlaceholder,
  showRefresh = false,
  onRefresh,
  refreshing = false,
  showSettings = false,
  onSettingsClick,
  headerActions,
  customFilters,
}) => {
  const { t } = useTranslation();

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle?.();
    }
  };

  const stopPropagation = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
  };

  const titleContent = (
    <div className="data-list-title">
      {icon && <i className={icon} />}
      {title}
      {typeof itemCount === 'number' && itemCount > 0 && (
        <span className="data-list-item-count">({itemCount})</span>
      )}
    </div>
  );

  const actionsContent = (
    <div className="data-list-header-actions-container" role="toolbar" onClick={stopPropagation} onKeyDown={stopPropagation}>
      {showSearch && (
        <span className="p-input-icon-right data-list-search-wrapper">
          <InputAtom
            value={searchValue}
            onChange={(e) => onSearchChange?.(e.target.value)}
            placeholder={searchPlaceholder || t('dataList.searchPlaceholder', 'Search...')}
            className="data-list-search-input"
            data-testid="data-list-search-input"
          />
          {searchValue ? (
            <button
              type="button"
              className="pi pi-times data-list-search-clear"
              aria-label={t('dataList.clearSearch', 'Clear search')}
              onClick={() => onSearchChange?.('')}
            ></button>
          ) : (
            <i className="pi pi-search" />
          )}
        </span>
      )}
      {customFilters && (
        <div className="data-list-header__custom-filters" data-testid="data-list-custom-filters">
          {customFilters}
        </div>
      )}
      <div className="data-list-header-actions">
        {showSettings && (
          <ButtonAtom
            icon="pi pi-cog"
            onClick={onSettingsClick}
            size="small"
            variant="secondary"
            outlined
            className="data-list-settings-button"
            tooltip={t('dataList.settings.title', 'Settings')}
            tooltipOptions={{ position: 'top' }}
            aria-label={t('dataList.settings.title', 'Settings')}
          />
        )}
        {showRefresh && (
          <ButtonAtom
            icon="pi pi-refresh"
            onClick={onRefresh}
            size="small"
            variant="secondary"
            outlined
            disabled={refreshing}
            className="data-list-refresh-button"
            tooltip={t('dataList.refresh', 'Refresh')}
            tooltipOptions={{ position: 'top' }}
            aria-label={t('dataList.refresh', 'Refresh')}
          />
        )}
        {headerActions}
      </div>
      {collapsible && (
        <i
          className={`pi pi-chevron-right data-list-toggle-icon ${isExpanded ? 'expanded' : ''}`}
        />
      )}
    </div>
  );

  if (collapsible) {
    return (
      <button
        type="button"
        className="data-list-header collapsible"
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        aria-expanded={isExpanded}
      >
        {titleContent}
        {actionsContent}
      </button>
    );
  }

  return (
    <div className="data-list-header">
      {titleContent}
      {actionsContent}
    </div>
  );
};

export default DataListHeader;
